/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { VodItem, VodItemList } from 'client-code/vod-browse';

import { SpinnerDelays } from '../../core/enums';
import { VodBrowseError } from '../../core/error/VodBrowseError';
import { InfoCardManager } from '../../core/infocard';
import { VodInteractor } from '../../core/interactors';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import { ScreenControllerTypes, ScreenTaskControllerTypes } from '../../core/navigation';
import type { AppDispatch, TGetState, TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import { TVodBrowseData } from '../../core/types';
import { VodBrowseInfoCardUserInputHandler } from '../../core/usecases/VodBrowseInfoCardUserInputHandler';
import { ErrorPriority } from '../../core/videoplayer/enums';
import {
    biaxialWidgetClearDataAction,
    biaxialWidgetUpdateHorizontalPositionAction,
    biaxialWidgetUpdateVerticalItemsAction,
} from '../biaxialwidget/biaxialwidget.actions';
import { BWHListType, THorizontalItem, TVerticalItems } from '../biaxialwidget/biaxialwidget.props';
import { biaxialWidgetUpdateHorizontalItems } from '../biaxialwidget/biaxialwidget.reducers';
import { OverlayTypes } from '../overlay/OverlayTypes';
import { showOverlayDialog } from '../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../overlaydialog/overlaydialogprops';
import { setScreenTaskController, showPreviousScreen } from '../screens/screens.reducers';
import { hideSpinner, showSpinner } from '../spinner/spinner.reducers';
import {
    TVodBrowseItems,
    vodBrowseMainScreenClearDataAction,
    vodBrowseMainScreenUpdateHorizontalItemsAction,
    vodBrowseMainScreenUpdateHorizontalPositionsAction,
    vodBrowseMainScreenUpdateVerticalItemsAction,
} from './vodbrowsemainscreen.actions';
import {
    VOD_BROWSE_MS_CLEAR_DATA,
    VOD_BROWSE_MS_SET_NEEDS_REFRESH,
    VOD_BROWSE_MS_UPDATE_HORIZONTAL_ITEMS,
    VOD_BROWSE_MS_UPDATE_HORIZONTAL_POSITIONS,
    VOD_BROWSE_MS_UPDATE_SHOULD_HIDE_ADULT_STATE,
    VOD_BROWSE_MS_UPDATE_VERTICAL_ITEMS,
} from './vodbrowsemainscreen.types';

const TAG: string = 'vodbrowsemainscreen.reducers';
const initialState: TVodBrowseItems = {
    horizontalItems: [],
    horizontalPositions: [],
    verticalItems: [],
    shouldHideAdultState: true,
    needsRefresh: false,
};

export const vodBrowseMainScreenReducers = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<TVodBrowseItems>>{},
): TVodBrowseItems => {
    switch (type) {
        case VOD_BROWSE_MS_CLEAR_DATA:
            return {
                ...state,
                horizontalItems: payload.horizontalItems,
                horizontalPositions: payload.horizontalPositions,
                verticalItems: payload.verticalItems,
                shouldHideAdultState: payload.shouldHideAdultState,
            };
        case VOD_BROWSE_MS_UPDATE_VERTICAL_ITEMS:
            return {
                ...state,
                verticalItems: payload.verticalItems,
            };

        case VOD_BROWSE_MS_UPDATE_HORIZONTAL_ITEMS:
            return {
                ...state,
                horizontalItems: payload.horizontalItems,
            };

        case VOD_BROWSE_MS_UPDATE_HORIZONTAL_POSITIONS:
            return {
                ...state,
                horizontalPositions: payload.horizontalPositions,
            };
        case VOD_BROWSE_MS_UPDATE_SHOULD_HIDE_ADULT_STATE:
            return {
                ...state,
                shouldHideAdultState: payload.shouldHideAdultState,
            };
        case VOD_BROWSE_MS_SET_NEEDS_REFRESH:
            return {
                ...state,
                needsRefresh: payload.needsRefresh,
            };

        default:
            return state;
    }
};

export const vodBrowseMainScreenClearData = () => (dispatch: Function) => {
    dispatch(vodBrowseMainScreenClearDataAction());
    dispatch(biaxialWidgetClearDataAction());
    Log.i(TAG, 'vodBrowseMainScreenClearData called');
};

export const vodBrowseMainScreenUpdateVerticalItems =
    () => async (dispatch: Function, getState: TGetState) => {
        const services = new VodInteractor();
        const { verticalPosition } = getState().biaxialWidget;
        try {
            const vodBrowseData: TVodBrowseData = await services.getVodBrowseData();
            const { vodItemList } = vodBrowseData;
            const verticalItems: Array<VodItem> = [];
            const biaxialVerticalItems: TVerticalItems = [];
            const horizontalPositions: Array<number> = [];
            vodItemList.vodItemList?.forEach((item: VodItem) => {
                verticalItems.push(item);
                horizontalPositions.push(0);
                biaxialVerticalItems.push({
                    displayTitle: item.title || '',
                    listTitle: item.title || '',
                });
            });
            dispatch(vodBrowseMainScreenUpdateVerticalItemsAction(verticalItems));
            dispatch(vodBrowseMainScreenUpdateHorizontalPositionsAction(horizontalPositions));
            dispatch(biaxialWidgetUpdateVerticalItemsAction(biaxialVerticalItems));
            Log.i(
                TAG,
                `vodBrowseMainScreenUpdateVerticalItems updated ${verticalItems.length} items`,
            );
            const parentVodItemId = verticalItems[verticalPosition]?.vodItemKernel.vodItemId ?? '';
            dispatch(vodBrowseMainScreenUpdateHorizontalItems(parentVodItemId, verticalPosition));
        } catch (error: any) {
            const { verticalItems } = getState().vodBrowseMainScreen;
            if (verticalItems.length !== 0) {
                const parentVodItemId =
                    verticalItems?.[verticalPosition]?.vodItemKernel?.vodItemId ?? '';
                dispatch(
                    vodBrowseMainScreenUpdateHorizontalItems(parentVodItemId, verticalPosition),
                );
                Log.i(TAG, 'Using cached vertical items due to error in fetching new data');
            } else {
                const vodBrowseServiceError = error?.response?.data;
                dispatch(showErrorOverlay(vodBrowseServiceError, showPreviousScreen()));
            }
            Log.e(TAG, 'Failed to update vertical items', error);
        }
    };

export const showErrorOverlay =
    (vodBrowseServiceError: any, onOverlayAutoHide?: Function) =>
    (dispatch: Function, getState: Function) => {
        const { screenController } = getState().screens;
        if (screenController !== ScreenControllerTypes.VOD_BROWSE_MAIN_SCREEN) {
            Log.i(TAG, 'Skipping VOD Browse error overlay — no longer on VOD Browse screen');
            return;
        }
        const { uiProperties } = getState().overlayDialog;
        const vodBrowseError = new VodBrowseError(
            vodBrowseServiceError?.code,
            vodBrowseServiceError?.message,
            Locale.getString(LocaleKeys.VOD_BROWSE_ERROR_OVERLAY_DESCRIPTION),
        );
        const description = vodBrowseError
            .getDescription()
            .replace('ERROR_CODE', vodBrowseError.getCode());
        if (uiProperties.overlayType === OverlayTypes.NO_NETWORK) return;
        const overlayDialogProps: IOverlayDialog = {
            errorPriority: ErrorPriority.GENERAL_RESTRICTIONS,
            title: vodBrowseError.getTitle(),
            options: [
                {
                    text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT),
                },
            ],
            messageCode: vodBrowseError.getCode(),
            description,
            hasDimmer: false,
            hasSeperator: false,
            overlayType: OverlayTypes.ERROR,
            onAutoHide: () => dispatch(onOverlayAutoHide),
        };
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.VOD_BROWSE_ERROR_OVERLAY_TASKS));
        dispatch(showOverlayDialog(overlayDialogProps));
    };

export const vodBrowseMainScreenUpdateHorizontalItems =
    (parentVodItemId: string, verticalPosition: number) =>
    async (dispatch: Function, getState: TGetState) => {
        const { horizontalItems, verticalItems } = getState().vodBrowseMainScreen;

        if (horizontalItems.length && horizontalItems[verticalPosition]?.vodItemList?.length) {
            const currentHItems: VodItemList = horizontalItems[verticalPosition];
            const biaxialHItem: THorizontalItem = {
                listType: BWHListType.VOD_BROWSE_MAIN,
                listItems: currentHItems.vodItemList || [],
                isEmpty: false,
            };
            dispatch(biaxialWidgetUpdateHorizontalItems(biaxialHItem));
            Log.i(
                TAG,
                `vodBrowseMainScreenUpdateHorizontalItems already has items for vertical position ${verticalPosition}`,
            );
            return;
        }

        // Show spinner before network call with 4s delay
        dispatch(showSpinner('', SpinnerDelays.VOD_BROWSE_MAIN_SCREEN));

        const services = new VodInteractor();
        try {
            const vodBrowseData: TVodBrowseData = await services.getVodBrowseData({
                parentVodItemId,
            });
            const { vodItemList } = vodBrowseData;

            if (vodItemList.vodItemList && vodItemList.vodItemList.length > 0) {
                vodItemList.vodItemList.push(verticalItems[verticalPosition]);
            }

            horizontalItems[verticalPosition] = vodItemList;
            const biaxialHItem: THorizontalItem = {
                listType: BWHListType.VOD_BROWSE_MAIN,
                listItems: vodItemList.vodItemList ?? [],
                isEmpty: false,
            };
            dispatch(biaxialWidgetUpdateHorizontalItems(biaxialHItem));
            dispatch(vodBrowseMainScreenUpdateHorizontalItemsAction(horizontalItems));
            Log.i(
                TAG,
                `vodBrowseMainScreenUpdateHorizontalItems updated ${horizontalItems[verticalPosition]?.vodItemList?.length} items`,
            );
        } catch (error) {
            const biaxialHItem: THorizontalItem = {
                listType: BWHListType.VOD_BROWSE_MAIN,
                listItems: [],
                isEmpty: true,
            };
            dispatch(biaxialWidgetUpdateHorizontalItems(biaxialHItem));
            Log.e(TAG, 'Failed to update horizontal items', error);
        } finally {
            // Hide spinner after network call completes
            dispatch(hideSpinner());
        }
    };

export const onInfoCardDismissVodBrowseMainScreen = () => (dispatch: AppDispatch) => {
    const infoCard = InfoCardManager.getInstance();
    const contextData = (infoCard.getContextData(VodBrowseInfoCardUserInputHandler.name) || {}) as {
        activeItemIndex: number;
        itemList: VodItem[];
    };
    dispatch(biaxialWidgetUpdateHorizontalPositionAction(contextData.activeItemIndex));
};

export default vodBrowseMainScreenReducers;
